import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import sh from "bundles/common/utils/sh";
import NewJobOfferCard from "bundles/Candidate/components/NewJobOfferCard";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";

const RelatedJobOffers = ({ jobOfferId }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale) || "en";

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const request = jobOfferId
      ? sh.get(`public_job_offers/${jobOfferId}/related`, {
        params: { authenticity_token: authenticityToken },
      })
      : sh.get("random_jobs", {
        params: { quantity: 4, locale: i18nLocale, authenticity_token: authenticityToken },
      });

    request
      .then((res) => {
        setOffers(res.data?.data || []);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [jobOfferId, authenticityToken, i18nLocale]);

  const humanizeSlug = (value) =>
    (value || "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const handleOfferClick = (offer) => {
    if (!offer?.id) return;

    const offerIndustry =
      Array.isArray(offer?.company_industries) && offer.company_industries.length > 0
        ? offer.company_industries[0]
        : null;
    const offerCategory =
      Array.isArray(offer?.categories) && offer.categories.length > 0
        ? offer.categories[0]
        : null;

    const crumbs = [
      {
        label: intl.formatMessage({ id: "menu.home", defaultMessage: "Home" }),
        path: `/${i18nLocale}`,
      },
      {
        label: intl.formatMessage({ id: "nav.job_offers", defaultMessage: "Job Offers" }),
        path: `/${i18nLocale}/job-offers`,
      },
    ];

    if (offerIndustry) {
      crumbs.push({
        label: intl.formatMessage({
          id: `sectors.${offerIndustry}`,
          defaultMessage: humanizeSlug(offerIndustry),
        }),
        path: `/${i18nLocale}/job-offers/${offerIndustry}`,
      });
    }

    if (offerIndustry && offerCategory) {
      crumbs.push({
        label: intl.formatMessage({
          id: `categories.${offerCategory}`,
          defaultMessage: humanizeSlug(offerCategory),
        }),
        path: `/${i18nLocale}/job-offers/${offerIndustry}/${offerCategory}`,
      });
    }

    navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <SquarehubLoader size={48} centered />
      </Box>
    );
  }

  if (offers.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Segoe UI Semibold, sans-serif",
            fontSize: "20px",
            color: "#023331",
          }}
        >
          <FormattedMessage id="public_job_page.related_offers" />
        </Typography>

        <Button
          onClick={() => (window.location.href = `/${i18nLocale}/job-offers`)}
          disableRipple
          sx={{
            textTransform: "none",
            p: 0,
            minWidth: "auto",
            fontSize: "14px",
            fontFamily: "Segoe UI Semibold, sans-serif",
            color: "#166d4c",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          <FormattedMessage id="public_job_page.see_all_offers" />
          {" \u2192"}
        </Button>
      </Box>

      <Grid container spacing={2}>
        {offers.map((offer) => (
          <Grid item xs={12} md={6} key={offer.id} sx={{ display: "flex" }}>
            <NewJobOfferCard offer={offer} onClick={handleOfferClick} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RelatedJobOffers;
